import React, { useEffect, useState } from "react";
import { Chart } from 'react-charts';
import { PieChart } from 'react-minimal-pie-chart';
import { Row, Col, Button, message, Spin } from "antd";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from "styled-components";
import * as XLSX from 'xlsx';
import StageProvider from "../../../../../../shared/provider/StageProvider";
import DashboardProvider from "../../../../../../shared/provider/DashboardProvider";
import { StageDto } from "../../../../../../shared/dto/StageDto";
import { DashboardIdeaStartedAndFinishedDto } from "../../../../../../shared/dto/DashboardIdeaStartedAndFinishedDto";
import { IdeaWithoutTreatmentDTO } from "../../../../../../shared/dto/IdeaWithoutTreatmentDto";
import IdeasWithoutEvaluationList from "../IdeasWithoutEvaluationList";
import TopIdeasWithEvaluationList from "../TopIdeasWithEvaluationList";
import LowEvaluationIdeasList from "../LowEvaluationIdeasList";
import FinishedIdeasList from "../FinishedIdeasList";
import { IdeaWithEvaluationAndWithoutTreatmentDTO } from "../../../../../../shared/dto/DashboardLastTenHighScoreDto";
import { DashboardIdeaWithoutEvaluation } from "../../../../../../shared/dto/DashboardIdeaWithoutEvaluation";
import { IdeaFinishedDTO } from "../../../../../../shared/dto/IdeaFinishedDTO";
import { RiFileExcel2Fill } from "react-icons/ri";

const ChartContainer = styled.div`
  background-color: #f0f0f0;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  max-height: 350px;
  height: 100%;
`;

const ChartTitle = styled.h3`
  margin-bottom: 8px;
`;

const YearNavigator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  margin-right: 8px;
`;

function Graficos(): JSX.Element {
  const [stageData, setStageData] = useState<StageDto[]>([{title: "teste", totalIdeas: 2, description: "teste", order: 1}]);
  const [finishedIdeasChartData, setFinishedIdeasChartData] = useState<DashboardIdeaStartedAndFinishedDto[]>([{month: "Jan", started: 2, finished: 1}]);
  const [ideasWithoutTreatment, setIdeasWithoutTreatment] = useState<IdeaWithoutTreatmentDTO>({ minorThenThree: 0, majorOrEqualThree: 0 });
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const [topIdeasWithEvaluation, setTopIdeasWithEvaluation] = useState<IdeaWithEvaluationAndWithoutTreatmentDTO[]>([]);
  const [lowEvaluationIdeas, setLowEvaluationIdeas] = useState<IdeaWithEvaluationAndWithoutTreatmentDTO[]>([]);
  const [ideasWithoutEvaluation, setIdeasWithoutEvaluation] = useState<DashboardIdeaWithoutEvaluation[]>([]);
  const [finishedIdeas, setFinishedIdeas] = useState<IdeaFinishedDTO[]>([]);

  const fetchStageData = async () => {
    const stageProvider = new StageProvider();
    try {
      const data = await stageProvider.getAll();
      setStageData(data || []);
    } catch (error) {
      console.error("Erro ao buscar dados das fases:", error);
    }
  };

  const fetchDashboardData = async (year: number) => {
    const dashboardProvider = new DashboardProvider();
    try {
      const data = await dashboardProvider.getIdeasStartedAndFinished(year);
      let filteredData = data;
      if (year === new Date().getFullYear()) {
        const currentMonthIndex = new Date().getMonth();
        filteredData = data.filter((item, index) => index <= currentMonthIndex);
      }
      setFinishedIdeasChartData(filteredData || []);
    } catch (error) {
      console.error("Erro ao buscar dados do dashboard:", error);
    }
  };

  const fetchIdeasWithoutTreatment = async () => {
    const dashboardProvider = new DashboardProvider();
    try {
      const data = await dashboardProvider.getIdeasWithoutTreatment();
      setIdeasWithoutTreatment(data || { minorThenThree: 0, majorOrEqualThree: 0 });
    } catch (error) {
      console.error("Erro ao buscar dados de ideias sem tratamento:", error);
    }
  };

  useEffect(() => {
    fetchStageData();
    fetchIdeasWithoutTreatment();
  }, []);

  useEffect(() => {
    fetchDashboardData(currentYear);
  }, [currentYear]);

  useEffect(() => {
    if (stageData.length && finishedIdeasChartData.length && ideasWithoutTreatment) {
      setLoading(false);
    }
  }, [stageData, finishedIdeasChartData, ideasWithoutTreatment]);

  const stageDataConfig = React.useMemo(
    () => [
      {
        label: 'Ideias por Fase',
        data: stageData.map(stage => ({
          primary: stage.title,
          secondary: stage.totalIdeas || 0,
        })),
      },
    ],
    [stageData]
  );

  const finishedIdeasChartDataConfig = React.useMemo(
    () => [
      {
        label: 'Ideias Iniciadas',
        data: finishedIdeasChartData.map(item => ({
          primary: item.month,
          secondary: item.started,
        })),
      },
      {
        label: 'Ideias Finalizadas',
        data: finishedIdeasChartData.map(item => ({
          primary: item.month,
          secondary: item.finished,
        })),
      },
    ],
    [finishedIdeasChartData]
  );

  const ideasWithoutTreatmentConfig = React.useMemo(
    () => [
      {
        title: 'Menor que 3',
        value: ideasWithoutTreatment.minorThenThree,
        color: '#ff7300',
      },
      {
        title: 'Maior ou igual a 3',
        value: ideasWithoutTreatment.majorOrEqualThree,
        color: '#1351b4',
      },
    ],
    [ideasWithoutTreatment]
  );

  const stagePrimaryAxis = React.useMemo(
    () => ({
      getValue: (datum: any) => datum.primary,
      type: 'ordinal',
    }),
    []
  );

  const stageSecondaryAxes = React.useMemo(
    () => [
      {
        getValue: (datum: any) => datum.secondary,
        type: 'linear',
        elementType: 'bar',
        tickFormat: (tick: number) => Math.round(tick),
        tickCount: 5,
        showGrid: false,
      },
    ],
    []
  );

  const finishedIdeasPrimaryAxis = React.useMemo(
    () => ({
      getValue: (datum: any) => datum.primary,
      type: 'ordinal',
    }),
    []
  );

  const finishedIdeasSecondaryAxes = React.useMemo(
    () => [
      {
        getValue: (datum: any) => datum.secondary,
        type: 'linear',
        elementType: 'bar',
        tickFormat: (tick: number) => Math.round(tick),
        tickCount: 2,
        showGrid: false,
      },
    ],
    []
  );

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const timestamp = new Date().toISOString().replace(/[-:.]/g, "");

    const addSheet = (data: any[], sheetName: string, columns: any[]) => {
      const formattedData = data.map(item => {
        const formattedItem: any = {};
        columns.forEach(column => {
          formattedItem[column.title] = item[column.dataIndex];
        });
        return formattedItem;
      });
      const ws = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };

    addSheet(ideasWithoutEvaluation, "Ideias sem Avaliação", [
      { title: 'ID', dataIndex: 'ideaId' },
      { title: 'Nome', dataIndex: 'ideaTitle' },
      { title: 'Nota', dataIndex: 'score' },
      { title: 'Dias desde a avaliação', dataIndex: 'daysAfterEvaluation' },
    ]);

    addSheet(topIdeasWithEvaluation, "Top Ideias com Avaliação", [
      { title: 'ID', dataIndex: 'ideaId' },
      { title: 'Nome', dataIndex: 'ideaTitle' },
      { title: 'Nota', dataIndex: 'score' },
      { title: 'Dias desde a avaliação', dataIndex: 'daysAfterEvaluation' },
    ]);

    addSheet(lowEvaluationIdeas, "Ideias com Baixa Avaliação", [
      { title: 'ID', dataIndex: 'ideaId' },
      { title: 'Nome', dataIndex: 'ideaTitle' },
      { title: 'Nota', dataIndex: 'score' },
      { title: 'Dias desde a avaliação', dataIndex: 'daysAfterEvaluation' },
    ]);

    addSheet(finishedIdeas, "Ideias Concluídas", [
      { title: 'ID', dataIndex: 'ideaId' },
      { title: 'Nome', dataIndex: 'ideaTitle' },
      { title: 'Nota', dataIndex: 'score' },
      { title: 'Dias desde a avaliação', dataIndex: 'daysAfterEvaluation' },
    ]);

    XLSX.writeFile(wb, `dashboard-${timestamp}.xlsx`);
    message.success("Dados exportados com sucesso!");
  };

  if (loading) {
    return <Spin />;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={8}>
          <ChartContainer>
            <ChartTitle>Quantidade de ideias por fase</ChartTitle>
            <div style={{ height: 200, width: '100%' }}>
              {stageData.length > 0 && (
                <Chart 
                  options={{
                    data: stageDataConfig,
                    primaryAxis: stagePrimaryAxis,
                    secondaryAxes: stageSecondaryAxes,
                    resizable: true,
                    defaultColors: ['#1351b4']
                  }}
                />
              )}
            </div>
            <LegendContainer>
              <LegendItem>
                <LegendColor color="#1351b4" />
                <span>Quantidade de ideias</span>
              </LegendItem>
            </LegendContainer>
          </ChartContainer>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <ChartContainer>
            <ChartTitle>Ideações iniciadas e concluídas</ChartTitle>
            <YearNavigator>
              <Button style={{height: 20, background: 'none', border: 0, borderRadius: 0}} icon={<LeftOutlined style={{fontSize: 12}} />} onClick={() => setCurrentYear(currentYear - 1)} />
              <span style={{ margin: '0 16px', fontSize: 12 }}>{currentYear}</span>
              <Button style={{height: 20, background: 'none', border: 0, borderRadius: 0}} icon={<RightOutlined style={{fontSize: 12}} />} onClick={() => setCurrentYear(currentYear + 1)} />
            </YearNavigator>
            <div style={{ height: 200, width: '100%' }}>
              {finishedIdeasChartData.length > 0 && (
                <Chart 
                  options={{
                    data: finishedIdeasChartDataConfig,
                    primaryAxis: finishedIdeasPrimaryAxis,
                    secondaryAxes: finishedIdeasSecondaryAxes,
                    resizable: true,
                    defaultColors: ['#1351b4', '#ff7300']
                  }}
                />
              )}
            </div>
            <LegendContainer>
              <LegendItem>
                <LegendColor color="#1351b4" />
                <span>Iniciadas</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff7300" />
                <span>Concluídas</span>
              </LegendItem>
            </LegendContainer>
          </ChartContainer>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <ChartContainer>
            <ChartTitle>Ideias sem tratamento</ChartTitle>
            <div style={{ height: 200, width: '100%' }}>
              {ideasWithoutTreatment.minorThenThree > 0 || ideasWithoutTreatment.majorOrEqualThree > 0 ? (
                <PieChart
                  data={ideasWithoutTreatmentConfig}
                  radius={42}
                  lineWidth={60}
                  segmentsStyle={{ transition: 'stroke .3s', cursor: 'pointer' }}
                  animate
                  label={({ dataEntry }) =>
                  `${Math.round(dataEntry.value)}`
                  }
                  labelStyle={(index) => ({
                  fill: "#fff",
                  fontSize: '10px',
                  fontFamily: 'sans-serif',
                  backgroundColor: '#808080',
                  borderRadius: '50%',
                  padding: '2px 5px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  })}
                  labelPosition={70}
                />
              ) : null}
            </div>
            <LegendContainer>
              <LegendItem>
                <LegendColor color="#1351b4" />
                <span>Nota {'>'}= 3</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff7300" />
                <span>Nota {'<'} 3</span>
              </LegendItem>
            </LegendContainer>
          </ChartContainer>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <h3 style={{textAlign: 'center'}}>Ideias aguardando avaliação</h3>
          <IdeasWithoutEvaluationList dataUpdater={setIdeasWithoutEvaluation} />
        </Col>
        <Col xs={24} sm={24} md={12}>
          <h3 style={{textAlign: 'center'}}>10 ideias com maiores notas ainda não tratadas</h3>
          <TopIdeasWithEvaluationList dataUpdater={setTopIdeasWithEvaluation} />
        </Col>
        <Col xs={24} sm={24} md={12}>
          <h3 style={{textAlign: 'center'}}>Ideias não tratadas com nota inferior a 3</h3>
          <LowEvaluationIdeasList dataUpdater={setLowEvaluationIdeas} />
        </Col>
        <Col xs={24} sm={24} md={12}>
          <h3 style={{textAlign: 'center'}}>Ideações realizadas nos últimos 12 meses</h3>
          <FinishedIdeasList dataUpdater={setFinishedIdeas} />
        </Col>
      </Row>
      <Row justify="end">
        <Button
          type="primary"
          icon={<RiFileExcel2Fill />}
          className="btn-responsivo"
          onClick={exportToExcel}
          style={{ marginBottom: 16, borderRadius: 16, backgroundColor: "#1351b4" }}
        >
          Exportar para Excel
        </Button>
      </Row>
    </div>
  );
}

export default Graficos;
